import React from 'react';
import 'primeicons/primeicons.css';
import './EsHomeRoadmap.css';
import { PHASE_LIST, planDate } from './EsRoadmapPlan';
import { useEsStrings } from '../../common/EsStrings';
import { esCopyTag } from '../../common/EsCopy';
import { esFormatDate } from '../../common/EsIntl';

// The plan's names in the reader's locale. PHASE_LIST stays English: its names are the testids'
// slugs and the welcome page's arrival lookup. A name missing here reads as the plan wrote it.
const planText = ( t ) => ( {
    'Tabletop Phase \u2014 Halloween Release': t( 'home-roadmap-phase-tabletop', 'Tabletop Phase \u2014 Halloween Release' ),
    'Virtual Phase \u2014 New Year\u2019s Release': t( 'home-roadmap-phase-virtual', 'Virtual Phase \u2014 New Year\u2019s Release' ),
    'Beta 1': t( 'home-roadmap-beta', 'Beta {number}', { number: '1' } ),
    'Beta 2': t( 'home-roadmap-beta', 'Beta {number}', { number: '2' } ),
    'Beta 3': t( 'home-roadmap-beta', 'Beta {number}', { number: '3' } ),
    Codex: t( 'home-roadmap-item-codex', 'Codex' ),
    Characters: t( 'home-roadmap-item-characters', 'Characters' ),
    Playtesting: t( 'home-roadmap-item-playtesting', 'Playtesting' ),
    Console: t( 'home-roadmap-item-console', 'Console' ),
    Tabletop: t( 'home-roadmap-item-tabletop', 'Tabletop' ),
    'Campaign Builder': t( 'home-roadmap-item-campaign-builder', 'Campaign Builder' ),
    'Homebrew Engine': t( 'home-roadmap-item-homebrew-engine', 'Homebrew Engine' ),
    Room: t( 'home-roadmap-item-room', 'Room' ),
    Multiplayer: t( 'home-roadmap-item-multiplayer', 'Multiplayer' ),
    Map: t( 'home-roadmap-item-map', 'Map' ),
    Workbench: t( 'home-roadmap-item-workbench', 'Workbench' ),
    IDE: t( 'home-roadmap-item-ide', 'IDE' ),
    Effects: t( 'home-roadmap-item-effects', 'Effects' ),
} );

// The plan's "Oct 31" and its month bands in the reader's locale. English keeps the plan's own
// spelling, as EsDate keeps the app's; another language reads Intl's ("31 ott", "10月31日").
// The English stays the band's testid slug.
const englishTag = () => {
    const aTag = esCopyTag();
    return !aTag || aTag.toLowerCase().startsWith( 'en' );
};
const shownDate = ( pLabel ) => ( englishTag() || !planDate( pLabel ) ? pLabel
    : esFormatDate( planDate( pLabel ), { day: 'numeric', month: 'short' } ) );
const shownMonth = ( pLabel ) => ( englishTag() || !planDate( pLabel + ' 1' ) ? pLabel
    : esFormatDate( planDate( pLabel + ' 1' ), { month: 'short' } ) );

// THE SIX BAND COLOURS ARE THEME TOKENS -- --color-roadmap-<tone> and its -ink,
// set per tone class in EsHomeRoadmap.css and edited in /view/colorsheet. They used
// to be picked here, by an admin, into this browser's localStorage
// (esRoadmapColors), with a palette and a swap arrow at each end of every band;
// a pick there reached nobody else. Now a pick is the site's or the account's.
export default function EsHomeRoadmap() {
    const t = useEsStrings();
    const aPlan = planText( t );
    const shown = ( pName ) => aPlan[ pName ] ?? pName;
    const phaseList = PHASE_LIST;

    // Kebab means KEBAB -- [a-z0-9] and hyphens, per the convention in
    // CLAUDE.md. A version is an id here, so 0.10 is es-home-roadmap-0-10-card
    // rather than carrying a dot into a data-testid.
    //
    // EsShellActions.requireHomeRoadmapRelease APPLIES THIS SAME RULE to the
    // name in the feature file. The two are a pair; changing one alone turns
    // every roadmap assertion into "no such element".
    const slug = ( pName ) => pName.toLowerCase()
        .replace( /[\u2019']/g, "" )
        .replace( /[^a-z0-9]+/g, "-" )
        .replace( /^-|-$/g, "" );
    const phaseTestId = ( pPhase, pPart ) => "es-home-roadmap-" + slug( pPhase.name ) + "-" + pPart;
    const betaTestId = ( pBeta, pPart ) => "es-home-roadmap-" + slug( pBeta.key ?? pBeta.name ) + "-" + pPart;
    const releaseTestId = ( pRelease, pPart ) => "es-home-roadmap-" + slug( pRelease.version ) + "-" + pPart;

    // THE ROW IS A RELEASE, and every cell above one names the row it starts on.
    //
    // EXPLICIT ROWS, and the reason is the two picker columns. The grid's own
    // sparse auto-placement carried this table until the swatches moved to the
    // outer edges, and it cannot carry it now: the right-hand swatch sits in
    // the LAST column and has to span rows that the release column has already
    // filled. Auto-placement only ever moves forwards, so it would drop that
    // cell three rows below the band it belongs to. A row number says where a
    // thing is and is not clever.
    //
    // The stylesheet unsets them below 40rem, so the markup order is still the
    // reading order on a phone -- which is why the cells are still emitted in
    // the order a person reads them. The month bands are the one exception:
    // they come out ahead of the betas, and the phone hides them.
    //
    // Months are still DERIVED from the release dates rather than declared
    // beside them: the one rule that has survived every version of this page,
    // because saying it twice is how the two come to disagree.
    let row = 1;

    const layout = phaseList.map( phase => {
        const bannerRow = row++;
        const bands = [];
        const betas = [];

        phase.betas.forEach( beta => {
            betas.push( { beta: beta, row: row, rows: beta.releases.length } );

            beta.releases.forEach( release => {
                const month = release.date.split( ' ' )[ 0 ];
                const open = bands[ bands.length - 1 ];
                if ( open && open.name === month ) open.rows += 1;
                // `open` is the first band of THIS PHASE, which is the one
                // that sits directly under a banner and must not draw its top
                // edge. It was tested as row === 1 and that silently stopped
                // being true when the rows became absolute: only the very first
                // band in the whole grid could ever match, so every phase after
                // the first drew a white rule immediately under its heading.
                else bands.push( { name: month, rows: 1, row: row, open: bands.length === 0 } );
                row += 1;
            } );
        } );

        return { phase: phase, bannerRow: bannerRow, bands: bands, betas: betas };
    } );

    const monthCell = ( pBand ) => (
        <div
            key={ pBand.name + pBand.row }
            data-testid={ "es-home-roadmap-" + slug( pBand.name ) }
            className={ "roadmapMonth" + ( pBand.open ? " roadmapMonthOpen" : "" ) }
            style={ { gridRow: pBand.row + " / span " + pBand.rows } }>
            <span>{ shownMonth( pBand.name ) }</span>
        </div>
    );

    return (
        <section id="roadmap" data-testid="es-home-roadmap" className="pt-[50px]">
            <div className="container">
                <div className="border-t border-section-rule pt-12">
                    <div className="flex flex-wrap mx-[-16px]">
                        <div className="w-full px-4">
                            <div className="sectionHeading wow fadeInUp" data-wow-delay=".1s" data-es-minis-beside="">
                                <h2 data-testid="es-home-roadmap-title" className="sectionTitle" > { t( 'home-roadmap-title', 'Roadmap' ) } </h2>
                                { /* The line under the title is EMPTY ON PURPOSE and keeps its
                                     box: the sentence it held ("Twelve pieces, a beta every
                                     three weeks...") was making a claim the calendar below
                                     already makes, but the gap it opens between the title and
                                     the first banner is wanted. The timeline sits INSIDE
                                     sectionHeading, so this line's mb-[18px], not the class's
                                     margin, is what centres the title between the hairline and
                                     the first banner, as every other section's is.
                                     A non-breaking space rather
                                     than a min-height, because the height is responsive --
                                     text-base becomes sm:text-lg -- and the character tracks
                                     that without restating either number. There is no
                                     data-testid: there is nothing to assert until words come
                                     back, and an id over an empty box invites a test that
                                     passes by finding whitespace. */ }
                                <p aria-hidden="true"
                                   className="mb-[18px] text-base font-medium leading-snug text-body sm:text-lg">
                                    &nbsp;
                                </p>

                                { /* This was <EsTimeline align="alternate">, and the date chips came
                                     out sitting high against their cards on every row.

                                     The cause was structural, not a spacing mistake. PrimeReact 11's
                                     .p-timeline-event-marker is a fixed-size dot -- width and height
                                     both --p-timeline-event-marker-size, with align-self: baseline --
                                     and this page was stuffing a whole date chip inside it. The chip
                                     overflowed the dot vertically CENTRED, so roughly half of it hung
                                     above the row's top edge, while the card in the content lane
                                     started at the top edge like a normal flex child. Two mechanisms,
                                     one row. EsHomeRoadmap.css was then adding margin-top: 1rem to the
                                     card, which pushed the pair a further 16px apart.

                                     EsTimeline is dropped rather than patched because the fix has to
                                     live in the marker's own sizing, and that is inside EsPrime.js. */ }
                                { /* WHY THE MARKUP IS IN THIS ORDER, which is not the reading order.
                                     Nothing here sets a grid-row NUMBER; every cell names its column
                                     in the stylesheet and its span here, and the grid's own sparse
                                     auto-placement puts it on a row. That algorithm moves down a row
                                     whenever the cursor's column goes BACKWARDS, so a month band --
                                     column 1 -- emitted after a piece -- column 3 -- starts on the
                                     row after that piece, and then slides past the band above it to
                                     the first free row. Emitting a band immediately before the piece
                                     it opens is therefore enough to place it, with one exception: a
                                     band that opens at a release's FIRST piece has to come before
                                     that release's own cell, or the release cell claims the row
                                     first and the band lands one row too low.

                                     Row numbers would have said all this in three lines. They are
                                     not used because they would have to be UNSAID below 40rem, where
                                     the grid collapses to one column and the markup order becomes
                                     the reading order -- and this order already reads correctly
                                     there once the month bands are hidden.

                                     One hook per piece, keyed on the piece's own title --
                                     es-home-roadmap-characters-card -- and per release on its name,
                                     because the twelve are the same markup twelve times and the
                                     list is edited every release. */ }
                                <div
                                    data-testid="es-home-roadmap-timeline"
                                    className="roadmapTimeline">
                                    { layout.map( ( { phase, bannerRow, bands, betas } ) => (
                                        <React.Fragment key={ phase.name }>
                                            { /* The band that OPENS a phase, spanning every column. */ }
                                            <div
                                                data-testid={ phaseTestId( phase, 'banner' ) }
                                                className="roadmapPhase"
                                                style={ { gridRow: bannerRow } }>
                                                <span className="roadmapPhaseName">{ shown( phase.name ) }</span>
                                                <span className="roadmapPhaseDate">{ shownDate( phase.date ) }</span>
                                            </div>

                                            { bands.map( monthCell ) }

                                            { betas.map( ( { beta, row, rows } ) => (
                                                <React.Fragment key={ beta.name }>
                                                    { /* The beta takes the LAST release in its group for its
                                                         date, because it IS that release -- 1.0 is Beta 3.
                                                         One date, written down once, so the two cannot
                                                         drift. */ }
                                                    <div
                                                        data-testid={ betaTestId( beta, 'card' ) }
                                                        className={ "roadmapBeta roadmapTone-" + beta.tone }
                                                        style={ { gridRow: row + " / span " + rows } }>
                                                        <span className="roadmapBetaName">
                                                            { shown( beta.name ) }
                                                        </span>
                                                        <span
                                                            data-testid={ betaTestId( beta, 'date' ) }
                                                            className="roadmapBetaDate">
                                                            { shownDate( beta.releases[ beta.releases.length - 1 ].date ) }
                                                        </span>
                                                    </div>

                                                    { beta.releases.map( ( release, index ) => (
                                                        <div
                                                            key={ release.version }
                                                            data-testid={ releaseTestId( release, 'card' ) }
                                                            className={ "roadmapRelease roadmapTone-" + beta.tone
                                                                + ( index === beta.releases.length - 1 ? " roadmapReleaseLast" : "" ) }
                                                            style={ { gridRow: row + index } }>
                                                            <span className="roadmapReleaseVersion">
                                                                { release.version }
                                                            </span>
                                                            <span
                                                                data-testid={ releaseTestId( release, 'date' ) }
                                                                className="roadmapReleaseDate">
                                                                { shownDate( release.date ) }
                                                            </span>
                                                            <span className="roadmapReleaseItems">
                                                                { release.items ? shown( release.items ) : '' }
                                                            </span>
                                                        </div>
                                                    ) ) }

                                                </React.Fragment>
                                            ) ) }
                                        </React.Fragment>
                                    ) ) }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
